import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DisplayedProduct } from '@coworkrz/products/data-access';
import { Cart, CartItem, MAX_MIN_QUANTITY } from './cart';
import { BehaviorSubject, Observable } from 'rxjs';
import { StorageService } from '@coworkrz/shared/misc';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private http = inject(HttpClient);
  private storage = inject(StorageService);

  private url = 'carts';
  private cartSubject = new BehaviorSubject<Cart>({
    cartItems: [],
    totalAmount: 0,
    totalItems: 0,
  });
  cart$ = this.cartSubject.asObservable();
  private cartKey = 'cart';

  constructor() {
    this.loadFromLocalStorage();
  }

  updateCart(items: CartItem[], saveToLocalStorage = false) {
    let totalAmount = 0;
    let totalItems = 0;
    items.forEach((item) => {
      totalAmount += +item.product.currentPrice.amount * item._quantity;
      totalItems += item.quantity;
    });

    // Mise à jour du cartSubject avec le nombre total d'articles
    this.cartSubject.next({ cartItems: items, totalAmount, totalItems });
    if (saveToLocalStorage)
      this.storage.setItem(this.cartKey, JSON.stringify(items));
  }

  updateItem(
    product: DisplayedProduct,
    quantity: number,
    quantityFactor = 1,
    saveToLocalStorage = false
  ): void {
    const items = [...this.cartSubject.value.cartItems];
    const index = items.findIndex(
      (item) => item.product.currentPrice.id === product.currentPrice.id
    );
    const isSubscription = product.currentPrice.isSubscription;
    const computedQuantity = (
      quantity: number,
      quantityFactor: number,
      isSubscription?: boolean
    ) => {
      return isSubscription ? quantity * quantityFactor : quantity;
    };
    const _quantity = computedQuantity(
      quantity,
      quantityFactor,
      isSubscription
    );

    if (index !== -1) {
      if (
        _quantity > MAX_MIN_QUANTITY.MIN &&
        _quantity <= MAX_MIN_QUANTITY.MAX
      ) {
        items[index].quantity = quantity;
        items[index].quantityFactor = quantityFactor;
        items[index]._quantity = computedQuantity(
          quantity,
          quantityFactor,
          isSubscription
        );
      } else if (_quantity <= MAX_MIN_QUANTITY.MIN) {
        items.splice(index, 1); // Remove item if quantity is less than MIN
      } // Ignore if quantity exceeds MAX
    } else if (_quantity > 0 && _quantity <= MAX_MIN_QUANTITY.MAX) {
      items.push({
        name: product.name,
        product,
        _quantity,
        quantity,
        quantityFactor,
        priceUnit: product.currentPrice.unit,
      }); // Add new item if within valid range
    }
    this.updateCart(items, saveToLocalStorage); // Update the entire cart state
  }

  deleteProduct(
    cartId: string,
    productId: string
  ): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `${this.url}/${cartId}/items/${productId}`
    ) as Observable<{ message: string }>;
  }

  save(cartId: string, cardNumber: string) {
    const cart = this.cartSubject.getValue();
    const parsedCart = cart.cartItems.map((cartItem) => ({
      quantity: cartItem.quantity,
      product: cartItem.product.id,
      priceUnit: cartItem.product.currentPrice.unit,
    }));
    return this.http.post(`${this.url}/${cartId}`, {
      cartItems: parsedCart,
      cardNumber,
    });
  }

  private computeTotalPrice(cart?: Cart) {
    const items = (cart ?? this.cartSubject.getValue()).cartItems;
    const totalAmount = items.reduce(
      (acc, item) => acc + +item.product.currentPrice.amount * item.quantity,
      0
    );
    const totalItems = items.reduce((acc, item) => acc + item.quantity, 0);
    this.cartSubject.next({ cartItems: items, totalAmount, totalItems });
  }

  private loadFromLocalStorage() {
    const storedCartItemsJson = this.storage.getItem(this.cartKey);
    const storedCartItems = storedCartItemsJson
      ? JSON.parse(storedCartItemsJson)
      : [];
    this.computeTotalPrice({
      cartItems: storedCartItems,
      totalAmount: 0,
      totalItems: 0,
    });
  }

  updateQuantityFactor(product: DisplayedProduct, factor: number) {
    const cartItem = this.cartSubject.value.cartItems.find(
      (item) => item.product.currentPrice.id === product.currentPrice.id
    )!;

    this.updateItem(product, cartItem.quantity, factor);
  }
}
